import { crc16ccitt } from "crc";

/*
 * Generating CRC ISO/IEC 13239 that required by EMV QR Documentation
 */
function crc(string) {
  const crcDec = crc16ccitt(string);

  const crcHex = crcDec
    .toString(16)
    .padStart(4, "0")
    .toUpperCase();

  return crcHex;
}

/*
 * EMV QR String generator
 * https://www.emvco.com/emv-technologies/qrcodes/
 */
const generate = {
  length(string) {
    return String(string.length).padStart(2, "0");
  },

  payloadFormatIndicator() {
    return "000201";
  },

  pointOfInitialMethod(amount) {
    // when amount provided, POI should have code 12
    if (amount) return "010212";

    // when amount is not provided, POI should have code 11
    return "010211";
  },

  merchantName(name) {
    return `59${this.length(name)}${name}`;
  },

  country(code) {
    return `58${this.length(code)}${code}`;
  },

  city(name) {
    return `60${this.length(name)}${name}`;
  },

  merchantCategory(code) {
    if (String(code).length !== 4)
      throw new TypeError("Merchant category code must have length equal to 4");

    return `5204${code}`;
  },

  merchantAccountInformation(identifierCode, identifierValue) {
    // hardcode for identifierCode = 29
    // pls rewrite it if implementation is needed for codes that differs from 29
    const recipientId = `00${this.length(identifierValue)}${identifierValue}`;
    return `${identifierCode}${this.length(recipientId)}${recipientId}`;
  },

  currency(currency) {
    if (currency !== "KHR" && currency !== "USD")
      throw new TypeError("Currency must be equal to 'KHR' or 'USD'");

    let currencyCode = null;
    switch (currency) {
      default:
      case "KHR":
        currencyCode = 116;
        break;
      case "USD":
        currencyCode = 840;
        break;
    }

    return `5303${currencyCode}`;
  },

  amount(value) {
    if (value === "0") throw new Error("Amount must not be equal to 0");
    if (!value) return "";

    const amount = String(value);

    return `54${this.length(amount)}${amount}`;
  },

  billNumber(billNumber) {
    if (!billNumber) return "";
    return `01${this.length(billNumber)}${billNumber}`;
  },

  description(description) {
    if (!description) return "";
    return `08${this.length(description)}${description}`;
  },

  additionalDataFieldTemplate(additionalDataObjects) {
    const body = additionalDataObjects.join("");
    if (!body) return "";

    return `62${this.length(body)}${body}`;
  },

  crc,
};

const generateEMV = ({
  /*
   * sets default values according to this document:
   * https://docs.google.com/document/d/12Ta0HB2gq7vH-SZ-QiQt_9WgkaZslsFCxvHkX_KshxQ/edit#heading=h.jaxmkea3ycrn
   */
  merchantName = "undefined",
  countryName = "KH",
  cityName = "Phnom Penh",
  merchantCategoryCode = "5999",
  recipientId,
  currencyName,
  currencyAmount = 0,
  billNumber = "",
  description = "",
} = {}) => {
  if (!recipientId)
    throw new TypeError("RecipientId is required during generation of EMV QR");

  const payloadFormatIndicator = generate.payloadFormatIndicator();
  const pointOfInitialMethod = generate.pointOfInitialMethod(currencyAmount);
  const merchantAccountInformation = generate.merchantAccountInformation(
    29,
    recipientId,
  );
  const merchantCategory = generate.merchantCategory(merchantCategoryCode);
  const currency = generate.currency(currencyName);
  const amount = generate.amount(currencyAmount);
  const merchant = generate.merchantName(merchantName);
  const country = generate.country(countryName);
  const city = generate.city(cityName);
  const billNumberDataOjbect = generate.billNumber(billNumber);
  const descriptionDataObject = generate.description(description);
  const additionalDataFieldTemplate = generate.additionalDataFieldTemplate([
    billNumberDataOjbect,
    descriptionDataObject,
  ]);

  const emv =
    payloadFormatIndicator +
    pointOfInitialMethod +
    merchantAccountInformation +
    merchantCategory +
    currency +
    amount +
    country +
    merchant +
    city +
    additionalDataFieldTemplate +
    "6304";

  const crc = generate.crc(emv);

  return emv + crc;
};

/*
 * EMV QR String parser
 */

function parseEMV(string) {
  checkStringCorrectnessByCrc(string);

  const emv = new EmvParser(string);

  return emv.result;
}

function checkStringCorrectnessByCrc(string) {
  const stringWithoutCrc = string.slice(0, -4);
  const checkCrc = crc(stringWithoutCrc);

  if (checkCrc !== string.slice(-4)) {
    throw new Error(
      "CRC error. Probably QR code scanned incorrectly. Please, try again.",
    );
  }
}

class EmvParser {
  constructor(string) {
    this.raw = string;
    this.parse(string);
  }

  result = {
    emvVersion: null,
    initiationMethod: null,
    recipientId: null,
    merchantCategoryCode: null,
    currency: null,
    amount: null,
    country: null,
    merchantName: null,
    city: null,
    billNumber: null,
    description: null,
    crc: null,
  };

  parse(string) {
    if (string.length) {
      const { id, data, rest } = this.parseDataObject(string);
      this.processDataObject(id, data);
      this.parse(rest);
    } else {
      this.checkResultCorrectness();
    }
  }

  parseDataObject(string) {
    const { id, length } = this.getIdAndLength(string);
    const data = string.substr(4, length);
    const rest = string.slice(4 + length);

    return { id, data, rest };
  }

  getIdAndLength(string) {
    const first4chars = string.slice(0, 4);
    const id = first4chars.slice(0, 2);
    const length = +first4chars.slice(2);

    return { id, length };
  }

  processDataObject(id, data) {
    switch (true) {
      case id === "00":
        return this.saveResult("emvVersion", data);
      case id === "01":
        return this.saveResult("initiationMethod", data);
      case parseInt(id) >= 26 && parseInt(id) <= 51:
        return this.saveResult(
          "recipientId",
          this.parseMerchantAccountInformation(data),
        );
      case id === "52":
        return this.saveResult("merchantCategoryCode", data);
      case id === "53":
        return this.saveResult("currency", this.getCurrencyByCode(data));
      case id === "54":
        return this.saveResult("amount", this.getAmount(data));
      case id === "58":
        return this.saveResult("country", data);
      case id === "59":
        return this.saveResult("merchantName", data);
      case id === "60":
        return this.saveResult("city", data);
      case id === "62":
        return this.parseAdditionalDataFields(data);
      case id === "63":
        return this.saveResult("crc", data);
      default:
        const error = new Error(
          `No scenario found for process data object with id '${id}' and payload '${data}'`,
        );
        console.error(error);
    }
  }

  parseMerchantAccountInformation(string) {
    const { data } = this.parseDataObject(string);

    return data;
  }

  parseAdditionalDataFields(string) {
    if (string.length) {
      const { id, data, rest } = this.parseDataObject(string);
      this.processAdditionalDataObjects(id, data);
      this.parseAdditionalDataFields(rest);
    }
  }

  processAdditionalDataObjects(id, data) {
    switch (id) {
      case "01":
        return this.saveResult("billNumber", data);
      case "08":
        return this.saveResult("description", data);
      default:
        const error = new Error(
          `No scenario found for process additional data object with id '${id}' and payload '${data}'`,
        );
        console.error(error);
    }
  }

  getCurrencyByCode(currencyCode) {
    switch (currencyCode) {
      default:
      case "840":
        return "USD";
      case "116":
        return "KHR";
    }
  }

  getAmount(string) {
    return Number(string);
  }

  saveResult(fieldName, data) {
    if (
      process.env.NODE_ENV === "development" &&
      !this.result.hasOwnProperty(fieldName)
    ) {
      console.error(
        `Result object doesn't contain a property with name '${fieldName}'. Probably you made a mistake.`,
      );
    }
    this.result[fieldName] = data;
  }

  checkResultCorrectness() {
    if (this.result.amount === 0) {
      throw new Error("Transaction amount tag '54' must not be equal to 0");
    }

    if (this.result.initiationMethod === "12" && !this.result.amount) {
      throw new Error("Dynamic code should contain amount tag '54'");
    }

    if (this.result.initiationMethod === "11" && this.result.amount) {
      throw new Error("Static code should not contain amount tag '54'");
    }
  }
}

export { generateEMV as generate, parseEMV as parse };
